// src/components/intentions/IntentionDetail.tsx
//
// Single source of truth for rendering ONE teaching-intention submission in full.
// mode='view' shows a rich read-only layout (all form sections). mode='edit'
// (Task 5) composes the same section components the submission form uses.
// Reused by /intentions and the HR review screen.

import React, { useState } from 'react';
import { User, Pencil, Save, X } from 'lucide-react';
import { TeachingIntention } from '../../types/intentions';
import { useAuth } from '../../contexts/AuthContext';
import { intentionsService } from '../../services/intentionsService';
import { AvailabilityData, PreferencesData, EmploymentDetailsData } from '../../types/intentionPeriods';
import { canEditIntentions, realActor } from '../../lib/intentionEditAccess';
import { displayLearningArea } from '../../constants/learningAreas';
import AvailabilitySection from './AvailabilitySection';
import PreferencesSection from './PreferencesSection';
import RUAHPreferencesSection from './RUAHPreferencesSection';
import EmploymentDetailsSection from './EmploymentDetailsSection';
import TILActivitiesSection from './TILActivitiesSection';

const FTE_PREF_LABELS: Record<string, string> = {
  '1': 'Full time (1.0)',
  '0.8': '0.8 FTE (1 day off per week)',
  '0.75': '0.75 FTE (1 day off per week)',
  '0.6': '0.6 FTE (2 days off per week)',
  '0.5': '0.5 FTE (2 days off per week)',
  '0.4': '0.4 FTE',
  discuss: 'Wants to discuss options'
};

const DAY_OFF_LABELS: Record<string, string> = {
  'wed-thu': 'Wednesday & Thursday off',
  'mon-fri': 'Monday & Friday off',
  'wed-only': 'Wednesday off',
  'fri-only': 'Friday off',
  none: 'No preference',
  discuss: 'Wants to discuss options'
};

const LEAVE_TYPE_LABELS: Record<string, string> = {
  'long-service': 'Long Service Leave',
  'leave-without-pay': 'Leave Without Pay',
  other: 'Other Leave'
};

function IntentionReadOnly({ intention }: { intention: TeachingIntention }) {
  const emp = intention.employmentDetails;
  const av = intention.availability;
  const prefs = intention.preferences;
  return (
    <div className="space-y-6">
      {/* Staff header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-navy-100 rounded-full flex items-center justify-center">
          <User className="w-6 h-6 text-navy-600" />
        </div>
        <div>
          <p className="font-medium text-ink-900">{intention.staffName}</p>
          <p className="text-sm text-ink-500">{intention.staffId} • {displayLearningArea(intention.department)}</p>
        </div>
      </div>

      {/* Employment & Return */}
      {emp && (
        <div>
          <h4 className="font-medium text-ink-900 mb-2">Employment &amp; Return</h4>
          <div className="bg-ink-50 rounded-lg p-4 space-y-2 text-sm">
            <p>
              <span className="text-ink-500">Employment type:</span>{' '}
              <span className="font-medium capitalize">{emp.employmentType || 'Not specified'}</span>
            </p>
            <p>
              <span className="text-ink-500">
                {emp.employmentType === 'ongoing' ? 'Intends to return:' : 'Wishes to be considered:'}
              </span>{' '}
              <span className="font-medium capitalize">
                {(emp.employmentType === 'ongoing' ? emp.intentionToReturn : emp.wishToBeConsidered) || 'No response'}
              </span>
            </p>
            {emp.notReturningReason && (
              <p><span className="text-ink-500">Reason / context:</span>{' '}<span>{emp.notReturningReason}</span></p>
            )}
            {emp.year9StretchInterest !== null && emp.year9StretchInterest !== undefined && (
              <p>
                <span className="text-ink-500">Year 9 Stretch interest:</span>{' '}
                <span className="font-medium">{emp.year9StretchInterest ? 'Interested' : 'Not interested'}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Planned Leave */}
      {emp?.hasLeavePlans && (
        <div>
          <h4 className="font-medium text-ink-900 mb-2">Planned Leave</h4>
          <div className="bg-ink-50 rounded-lg p-4 space-y-2 text-sm">
            <p>
              <span className="text-ink-500">Type:</span>{' '}
              <span className="font-medium">{LEAVE_TYPE_LABELS[emp.leaveType || ''] || 'Not specified'}</span>
            </p>
            {(emp.leaveStartDate || emp.leaveEndDate) && (
              <p>
                <span className="text-ink-500">Dates:</span>{' '}
                <span className="font-medium">
                  {emp.leaveStartDate ? new Date(emp.leaveStartDate).toLocaleDateString('en-AU') : '—'}
                  {' – '}
                  {emp.leaveEndDate ? new Date(emp.leaveEndDate).toLocaleDateString('en-AU') : '—'}
                </span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Availability */}
      <div>
        <h4 className="font-medium text-ink-900 mb-2">Availability</h4>
        <div className="bg-ink-50 rounded-lg p-4 space-y-2 text-sm">
          <p>
            <span className="text-ink-500">Preferred load:</span>{' '}
            <span className="font-medium">
              {FTE_PREF_LABELS[av?.ftePreference || ''] ||
                (av?.fte ? `${Math.round(av.fte * 100)}%` : 'Not specified')}
            </span>
          </p>
          {av?.dayOffPreference && (
            <p>
              <span className="text-ink-500">Day off preference:</span>{' '}
              <span className="font-medium">{DAY_OFF_LABELS[av.dayOffPreference] || av.dayOffPreference}</span>
            </p>
          )}
          {av?.leavePlans && (
            <p><span className="text-ink-500">Leave / sabbatical notes:</span>{' '}<span>{av.leavePlans}</span></p>
          )}
        </div>
      </div>

      {/* Teaching Preferences */}
      <div>
        <h4 className="font-medium text-ink-900 mb-2">Teaching Preferences</h4>
        <div className="bg-ink-50 rounded-lg p-4 space-y-3 text-sm">
          <div>
            <p className="text-ink-500 mb-1">Learning areas (ranked):</p>
            {prefs?.curriculumAreas && prefs.curriculumAreas.length > 0 ? (
              <ol className="list-decimal list-inside space-y-1">
                {[...prefs.curriculumAreas].sort((a, b) => a.rank - b.rank).map((area) => (
                  <li key={area.areaId} className="font-medium">{area.areaName}</li>
                ))}
              </ol>
            ) : (
              <p className="text-ink-500">None specified</p>
            )}
          </div>
          {prefs?.subjects && prefs.subjects.length > 0 && (
            <div>
              <p className="text-ink-500 mb-1">Subjects (ranked):</p>
              <ol className="list-decimal list-inside space-y-1">
                {[...prefs.subjects].sort((a, b) => a.rank - b.rank).map((s) => (
                  <li key={s.subjectId} className="font-medium">{s.subjectName}</li>
                ))}
              </ol>
            </div>
          )}
          {prefs?.ruahSubjects && prefs.ruahSubjects.length > 0 && (
            <div>
              <p className="text-ink-500 mb-1">RUAH subjects (ranked):</p>
              <ol className="list-decimal list-inside space-y-1">
                {[...prefs.ruahSubjects].sort((a, b) => a.rank - b.rank).map((s) => (
                  <li key={s.subjectId} className="font-medium">{s.subjectName}</li>
                ))}
              </ol>
            </div>
          )}
          {prefs?.yearLevels && prefs.yearLevels.length > 0 && (
            <p>
              <span className="text-ink-500">Year levels:</span>{' '}
              <span className="font-medium">{prefs.yearLevels.map((y) => y.replace('year', 'Year ')).join(', ')}</span>
            </p>
          )}
        </div>
      </div>

      {/* TIL Activities */}
      {emp?.tilActivities && emp.tilActivities.length > 0 && (
        <div>
          <h4 className="font-medium text-ink-900 mb-2">
            TIL Activities
            <span className="ml-2 text-xs font-normal text-ink-500">({emp.tilActivities.length} selected)</span>
          </h4>
          <div className="bg-ink-50 rounded-lg p-4">
            <div className="flex flex-wrap gap-2">
              {emp.tilActivities.map((activity, i) => (
                <span key={i} className="inline-flex items-center px-3 py-1 bg-white border border-ink-200 text-ink-900 rounded-full text-xs">
                  {activity}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Additional Comments */}
      {intention.additionalComments && (
        <div>
          <h4 className="font-medium text-ink-900 mb-2">Additional Comments</h4>
          <div className="bg-ink-50 rounded-lg p-4">
            <p className="text-sm whitespace-pre-wrap">{intention.additionalComments}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export interface IntentionDetailProps {
  intention: TeachingIntention;
  /** Start in view or edit (default 'view'). */
  initialMode?: 'view' | 'edit';
  /** Called after a successful save with the updated intention. */
  onSaved?: (updated: TeachingIntention) => void;
}

const IntentionDetail: React.FC<IntentionDetailProps> = ({ intention, initialMode = 'view', onSaved }) => {
  const { user } = useAuth();
  const canEdit = canEditIntentions(user) && !!intention.periodId;
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [availability, setAvailability] = useState<AvailabilityData>(
    intention.availability ?? { fte: intention.currentFTE ?? 1.0 }
  );
  const [preferences, setPreferences] = useState<PreferencesData>(
    intention.preferences ?? { curriculumAreas: [], subjects: [], yearLevels: [], ruahSubjects: [] }
  );
  const [employmentDetails, setEmploymentDetails] = useState<EmploymentDetailsData>(
    intention.employmentDetails ?? {
      employmentType: 'ongoing',
      intentionToReturn: null,
      wishToBeConsidered: null,
      hasLeavePlans: false,
      year9StretchInterest: null,
      tilActivities: []
    }
  );
  const [comments, setComments] = useState(intention.additionalComments ?? '');

  const handleSave = async () => {
    if (!intention.periodId) {
      setError('This intention has no submission period, so it cannot be edited here.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const derivedReturn =
        employmentDetails.employmentType === 'ongoing'
          ? employmentDetails.intentionToReturn || 'uncertain'
          : employmentDetails.wishToBeConsidered || 'uncertain';

      const updated: TeachingIntention = {
        ...intention,
        availability,
        preferences,
        employmentDetails,
        additionalComments: comments,
        currentFTE: availability.fte,
        targetFTE: availability.fte,
        intentionToReturn: derivedReturn,
        comfortableTeachingAreas: preferences.curriculumAreas.map((a) => a.areaName),
        lastUpdatedAt: new Date()
      };

      await intentionsService.updateIntentionByAdmin(updated, intention.periodId, realActor(user));
      setMode('view');
      onSaved?.(updated);
    } catch (e: any) {
      setError(e?.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (mode === 'view') {
    return (
      <div className="space-y-4">
        {canEdit && (
          <div className="flex justify-end">
            <button
              onClick={() => setMode('edit')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-navy-600 hover:text-navy-800"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          </div>
        )}
        <IntentionReadOnly intention={intention} />
      </div>
    );
  }

  const academicYear = intention.year;
  return (
    <div className="space-y-6">
      <EmploymentDetailsSection employmentDetails={employmentDetails} onChange={setEmploymentDetails} disabled={saving} />
      <TILActivitiesSection
        selectedActivities={employmentDetails.tilActivities}
        onChange={(activities) => setEmploymentDetails({ ...employmentDetails, tilActivities: activities })}
        disabled={saving}
      />
      <AvailabilitySection availability={availability} onChange={setAvailability} disabled={saving} />
      <PreferencesSection preferences={preferences} onChange={setPreferences} academicYear={academicYear} disabled={saving} />
      <RUAHPreferencesSection preferences={preferences} onChange={setPreferences} academicYear={academicYear} disabled={saving} />

      <div>
        <label className="block font-medium text-ink-900 mb-2">Additional Comments</label>
        <textarea
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          disabled={saving}
          rows={3}
          className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-navy-500 focus:border-navy-500"
        />
      </div>

      {error && <div className="p-3 bg-danger-bg border border-danger-border rounded text-sm text-danger-fg">{error}</div>}

      <div className="flex items-center justify-end gap-3">
        <button onClick={() => { setMode('view'); setError(null); }} disabled={saving} className="flex items-center gap-2 px-4 py-2 text-ink-700 hover:text-ink-900">
          <X className="w-4 h-4" /> Cancel
        </button>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default IntentionDetail;
